import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Award, Crown, Medal, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import AchievementManager from '../components/AchievementManager';
import { getBackendUrl } from '../utils/getBackendUrl';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const COLOR_BG = {
  amber:   'bg-amber-100 text-amber-800 border-amber-200',
  blue:    'bg-blue-100 text-blue-800 border-blue-200',
  indigo:  'bg-indigo-100 text-indigo-800 border-indigo-200',
  purple:  'bg-purple-100 text-purple-800 border-purple-200',
  rose:    'bg-rose-100 text-rose-800 border-rose-200',
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  red:     'bg-red-100 text-red-800 border-red-200',
  cyan:    'bg-cyan-100 text-cyan-800 border-cyan-200',
};

function initials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

function rankIcon(idx) {
  if (idx === 0) return <Crown className="w-5 h-5 text-yellow-500" />;
  if (idx === 1) return <Medal className="w-5 h-5 text-gray-400" />;
  if (idx === 2) return <Medal className="w-5 h-5 text-amber-700" />;
  return <span className="text-sm font-bold text-gray-400 w-5 text-center">{idx + 1}</span>;
}

export default function Leaderboard({ user }) {
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Admin awarding state
  const [showAward, setShowAward] = useState(null); // user row, or 'new' for user picker
  const [pickUserId, setPickUserId] = useState('');
  const [pickSlug, setPickSlug] = useState('');
  const [showHowTo, setShowHowTo] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  const fetchData = async () => {
    try {
      const [lb, cat] = await Promise.all([
        axios.get(`${API}/badges/leaderboard`),
        axios.get(`${API}/badges/catalog`),
      ]);
      setRows(Array.isArray(lb.data) ? lb.data : []);
      setCatalog(Array.isArray(cat.data) ? cat.data : []);
      if (isAdmin) {
        const u = await axios.get(`${API}/users`);
        setAllUsers((Array.isArray(u.data) ? u.data : []).filter((x) => ['employee', 'supervisor', 'sub_contractor'].includes(x.role)));
      }
    } catch {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const award = async () => {
    const targetId = showAward === 'new' ? pickUserId : showAward?.user_id;
    if (!targetId) { toast.error('Pick a team member first'); return; }
    if (!pickSlug) { toast.error('Pick a badge first'); return; }
    try {
      const { data } = await axios.post(`${API}/badges/award`, { user_id: targetId, slug: pickSlug });
      if (data.awarded) toast.success('Badge awarded');
      else toast('They already have that badge', { icon: 'ℹ️' });
      setShowAward(null);
      setPickUserId('');
      setPickSlug('');
      fetchData();
    } catch {
      toast.error('Failed to award');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-28">
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-6 h-6 text-yellow-500" />
          <h1 className="text-xl font-bold text-gray-900">Achievement Leaderboard</h1>
        </div>
        <p className="text-sm text-gray-500 mb-4">Top team members by badges earned.</p>

        {/* Admin: manage achievements */}
        {isAdmin && <AchievementManager catalog={catalog} onChanged={fetchData} />}

        {/* Admin: award badge to anyone (incl. employees not yet on the board) */}
        {isAdmin && (
          <button
            onClick={() => { setShowAward('new'); setPickUserId(''); setPickSlug(''); }}
            className="w-full mb-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 flex items-center justify-center gap-2"
            data-testid="award-any-badge-btn"
          >
            <Award className="w-4 h-4" /> Award a Badge to a Team Member
          </button>
        )}

        {/* How to earn badges */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden" data-testid="how-to-earn-card">
          <button
            onClick={() => setShowHowTo((s) => !s)}
            className="w-full flex items-center justify-between px-4 py-3"
            data-testid="how-to-earn-toggle"
          >
            <span className="flex items-center gap-2 text-sm font-bold text-gray-900">
              <HelpCircle className="w-4 h-4 text-blue-500" />
              How to earn badges
            </span>
            {showHowTo ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {showHowTo && (
            <div className="px-4 pb-4 space-y-1.5" data-testid="how-to-earn-list">
              {catalog.map((b) => {
                const autoEarned = !!b.auto_rule;
                return (
                  <div key={b.slug} className="flex items-start gap-2.5 p-2 rounded-lg bg-gray-50 border border-gray-100">
                    <span className="text-xl leading-none mt-0.5">{b.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-gray-900">{b.label}</p>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${autoEarned ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
                          {autoEarned ? 'EARNED AUTOMATICALLY' : 'AWARDED BY ADMIN'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{b.desc}</p>
                    </div>
                  </div>
                );
              })}
              <p className="text-[11px] text-gray-400 pt-1">
                Hour badges come from your logged time entries, photo badges from approved photo uploads. Keep working — badges update automatically!
              </p>
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-center text-sm text-gray-500 py-12">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="text-center py-20">
            <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No badges awarded yet.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="leaderboard-rows">
            {rows.map((r, idx) => (
              <div key={r.user_id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex items-center gap-3" data-testid={`lb-row-${r.user_id}`}>
                <div className="w-8 flex-shrink-0 flex items-center justify-center">{rankIcon(idx)}</div>
                {r.profile_picture ? (
                  <img src={r.profile_picture.startsWith('http') ? r.profile_picture : `${BACKEND_URL}${r.profile_picture}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold flex items-center justify-center text-sm">
                    {initials(r.name)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-gray-900 truncate">{r.name}</p>
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide">{r.role}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {r.badges.map((b) => (
                      <span key={b.slug} title={b.label} className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${COLOR_BG[b.color] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                        {b.icon} {b.label}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-yellow-600 leading-none">{r.badge_count}</p>
                  <p className="text-[10px] text-gray-500">badges</p>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => { setShowAward(r); setPickSlug(''); }}
                    className="text-xs font-bold text-blue-600 hover:text-blue-700 ml-1"
                    data-testid={`award-${r.user_id}`}
                  >
                    + Award
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAward && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setShowAward(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900">
              {showAward === 'new' ? 'Award a badge' : `Award a badge to ${showAward.name}`}
            </h3>
            {showAward === 'new' && (
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">Team member</label>
                <select
                  value={pickUserId}
                  onChange={(e) => setPickUserId(e.target.value)}
                  className="w-full p-2.5 rounded-lg border border-gray-300 text-sm text-gray-900 bg-white"
                  data-testid="award-user-select"
                >
                  <option value="">Choose a team member…</option>
                  {allUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role.replace('_', '-')})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {catalog.map((b) => (
                <button
                  key={b.slug}
                  onClick={() => setPickSlug(b.slug)}
                  className={`w-full text-left p-2.5 rounded-lg border flex items-start gap-2 ${pickSlug === b.slug ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                  data-testid={`pick-badge-${b.slug}`}
                >
                  <span className="text-xl">{b.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">{b.label}</p>
                    <p className="text-xs text-gray-500">{b.desc}</p>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <button onClick={() => setShowAward(null)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-bold">Cancel</button>
              <button onClick={award} disabled={!pickSlug || (showAward === 'new' && !pickUserId)} className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold disabled:bg-gray-200" data-testid="confirm-award-btn">
                Award
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
